import React, { Component, ReactNode } from 'react';
import { errorRequest } from 'core/services/product';
import { store } from 'core/store/configureStore';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Catches any render error in the app, ships it to
 * the error log (`/api/error` via `errorRequest`) and shows a recoverable
 * fallback instead of React Router's default "Unexpected Application Error!".
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    store.dispatch(
      errorRequest({
        code: 1,
        message: error?.message || 'Unknown application error',
        stackTrace: error?.stack || 'unknown',
        context: JSON.stringify({ componentStack: errorInfo?.componentStack, url: window.location.href }),
      }),
    );
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="max-w-md text-center p-6 bg-white dark:bg-howio-900 rounded-lg shadow-lg">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Something went wrong</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              An unexpected error occurred. The issue has been reported. Try reloading the page.
            </p>
            <button
              onClick={this.handleReload}
              className="w-full px-4 py-2 bg-brand text-brandtxt rounded hover:opacity-90 transition-opacity"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
